import { useCallback, useMemo } from 'react';
import { size } from 'lodash-es';
import { useTranslation } from 'react-i18next';
import { dynamicColumnCells, dynamicColumnMobileLines, dynamicColumns, filterKeyChanges, filtersWithoutHiddenColumns, parseDynamicColumnFilters, serializeDynamicColumnFilters } from '~/components/DynamicColumns/DynamicColumns.utils';
import { useTableColumnsLayout } from '~/components/TableColumnsLayout/hooks/useTableColumnsLayout';
import { useStateFromUrl } from '~/hooks/useStateFromUrl';
import { useLocaleKeys } from '~/i18n';
import { toSupportedLocale } from '~/i18n/locales';
import { referenceFilterConfig, referenceFilterGroups, referenceFilterOptionValues, withSelectAllMarked } from '../ClientsPage.filters';
import { clientReferenceFieldsColumnSource, SELECT_ALL_REFERENCE_FILTER, formatCountForLocale, isSelectAllReferenceFilter, referenceColumnId, referenceFilterSelection, referenceFiltersOf } from '../ClientsPage.referenceFields.utils';
import { CLIENT_SUMMARY_COLUMN_NAMES } from '../ClientsPage.table.utils';
const source = clientReferenceFieldsColumnSource;
export function useClientsReferenceFields(param) {
    let { data: { fields, values }, mainColumn, columns, onFiltersChange } = param;
    const { t: texts } = useLocaleKeys('client.dashboard');
    const { t: tableFilterTexts } = useLocaleKeys('referenceFields.tableFilters');
    const { i18n } = useTranslation();
    const { urlState: { referenceFilters: rawFilters }, changeUrlState } = useStateFromUrl({
        referenceFilters: {
            defaultValue: ''
        }
    });
    const keys = useMemo(()=>fields.map((param)=>{
            let { key } = param;
            return key;
        }), [
        fields
    ]);
    const columnIds = useMemo(()=>keys.map(referenceColumnId), [
        keys
    ]);
    const filters = useMemo(()=>parseDynamicColumnFilters(rawFilters, keys), [
        rawFilters,
        keys
    ]);
    const filteredColumnIds = useMemo(()=>Object.keys(filters).map(referenceColumnId), [
        filters
    ]);
    const sortIds = useMemo(()=>[
            ...CLIENT_SUMMARY_COLUMN_NAMES,
            ...columnIds
        ], [
        columnIds
    ]);
    const allColumns = useMemo(()=>[
            ...columns,
            ...dynamicColumns(source, fields)
        ], [
        columns,
        fields
    ]);
    const formatCount = (count)=>formatCountForLocale(count, toSupportedLocale(i18n.language));
    const writeFilters = (next)=>{
        changeUrlState({
            referenceFilters: serializeDynamicColumnFilters(next)
        });
        onFiltersChange();
    };
    const columnsLayout = useTableColumnsLayout({
        tableId: undefined,
        mainColumn,
        columns: allColumns,
        initiallyHiddenColumnIds: columnIds,
        filteredColumnIds,
        onLayoutChange: (hiddenColumnIds)=>{
            const kept = filtersWithoutHiddenColumns(source, filters, hiddenColumnIds);
            if (size(kept) !== size(filters)) writeFilters(kept);
        },
        panelBi: {
            triggerBi: {
                name: 'clients_columns_panel'
            },
            bi: {
                toggleVisibility: {
                    name: 'clients_columns_panel_toggle'
                },
                reorder: {
                    name: 'clients_columns_panel_reorder'
                },
                search: {
                    name: 'clients_columns_panel_search'
                }
            }
        }
    });
    const mobileFields = useMemo(()=>dynamicColumnMobileLines(source, fields, columnsLayout.shownColumnIds), [
        fields,
        columnsLayout.shownColumnIds
    ]);
    const filterLabels = useMemo(()=>Object.fromEntries(fields.map((param)=>{
            let { key, label } = param;
            return [
                referenceColumnId(key),
                label
            ];
        })), [
        fields
    ]);
    const filterGroups = useMemo(()=>referenceFilterGroups(fields, tableFilterTexts.groupLabel()), [
        fields
    ]);
    const cells = useMemo(()=>(client)=>dynamicColumnCells(source, fields, client), [
        fields
    ]);
    const filterConfig = referenceFilterConfig({
        fields,
        values,
        filters,
        notSetLabel: tableFilterTexts.notSet(),
        helperTexts: {
            truncatedHelper: (count)=>texts.referenceFieldFilter.truncatedHelper({
                    count: formatCount(count)
                }),
            maxValuesHelper: (count)=>texts.referenceFieldFilter.maxValuesHelper({
                    count: formatCount(count)
                }),
            maxValuesOrAllHelper: (count)=>texts.referenceFieldFilter.maxValuesOrAllHelper({
                    count: formatCount(count)
                })
        }
    });
    const selectedFilters = referenceFilterSelection(filters, (key)=>values[key].loaded ? referenceFilterOptionValues(filterConfig[referenceColumnId(key)]) : [
            ...SELECT_ALL_REFERENCE_FILTER
        ]);
    const loadingSelectAllColumnIds = useMemo(()=>new Set(Object.entries(filters).filter((param)=>{
            let [key, picked] = param;
            return isSelectAllReferenceFilter(picked) && values[key].loading;
        }).map((param)=>{
            let [key] = param;
            return referenceColumnId(key);
        })), [
        filters,
        values
    ]);
    const formatFilterLabel = useCallback((filter, baseLabel)=>loadingSelectAllColumnIds.has(filter) ? tableFilterTexts.loadingFilterLabel({
            label: baseLabel
        }) : undefined, [
        loadingSelectAllColumnIds
    ]);
    const formatFilterDescription = useCallback((filter)=>loadingSelectAllColumnIds.has(filter) ? tableFilterTexts.loadingValues() : undefined, [
        loadingSelectAllColumnIds
    ]);
    const changeFilters = (action)=>{
        const selection = typeof action === 'function' ? action(selectedFilters) : action;
        const next = withSelectAllMarked({
            selection: referenceFiltersOf(selection),
            previous: filters,
            values,
            filterConfig
        });
        if (serializeDynamicColumnFilters(next) === serializeDynamicColumnFilters(filters)) return;
        const { added, removed } = filterKeyChanges(filters, next);
        columnsLayout.followFilteredColumns({
            addedColumnIds: added.map(referenceColumnId),
            removedColumnIds: removed.map(referenceColumnId)
        });
        writeFilters(next);
    };
    return {
        sortIds,
        filters,
        columns: allColumns,
        layout: columnsLayout.layout,
        changeLayout: columnsLayout.changeLayout,
        columnsPanel: columnsLayout.columnsPanel,
        cells,
        mobileFields,
        filterLabels,
        filterConfig,
        filterGroups,
        selectedFilters,
        formatFilterLabel,
        formatFilterDescription,
        changeFilters,
        clearFilters: ()=>writeFilters({})
    };
}
